import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Phone, Clock, DollarSign, Building, Percent, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InquiryForm from '../components/InquiryForm';
import loanDetailContent from '../data/loanDetailContent';
import { seoMeta } from '../data/loanDetailContent';

const iconMap = {
  'Loan Term': Clock,
  'LTV': Percent,
  'Min Loan': DollarSign,
  'Max Loan': DollarSign,
  'Property Types': Building,
  'Closing Time': Clock,
  'Payments': FileText,
  'Rehab Financing': DollarSign,
  'Construction Budget': DollarSign,
  'Experience': FileText,
  'Land LTV': Percent,
  'Income Based': FileText,
  'DSCR': Percent,
};

const LoanDetailPage = () => {
  const { slug } = useParams();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const content = loanDetailContent[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!content) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Loan Product Not Found</h1>
          <Link to="/" className="text-blue-600 hover:underline">Return to Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const meta = seoMeta[slug] || {};

  return (
    <div className="min-h-screen" data-testid="loan-detail-page">
      <Helmet>
        <title>{meta.title || `${content.subtitle} Loans | Close Now Lending`}</title>
        <meta name="description" content={meta.description || content.introduction.substring(0, 160)} />
        <meta name="keywords" content={meta.keywords || ''} />
        <link rel="canonical" href={`https://closenowlending.com/loan-programs/${slug}`} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title || `${content.subtitle} Loans | Close Now Lending`} />
        <meta property="og:description" content={meta.description || content.introduction.substring(0, 160)} />
        <meta property="og:url" content={`https://closenowlending.com/loan-programs/${slug}`} />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title || `${content.subtitle} Loans | Close Now Lending`} />
        <meta name="twitter:description" content={meta.description || content.introduction.substring(0, 160)} />

        {/* Loan Product Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FinancialProduct",
          "name": content.subtitle + " Loans",
          "description": meta.description || content.introduction.substring(0, 300),
          "provider": {
            "@type": "FinancialService",
            "name": "Close Now Lending Corp",
            "url": "https://closenowlending.com"
          },
          "url": `https://closenowlending.com/loan-programs/${slug}`
        })}</script>
      </Helmet>
      <Header />

      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] pt-16" data-testid="loan-detail-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${content.heroImage})` }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 h-full flex flex-col justify-end pb-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/#loan-programs" className="hover:text-white transition-colors">Loan Products</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-blue-400">{content.subtitle}</span>
          </nav>
          <span className="inline-block bg-blue-600 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded mb-4 w-fit">
            {content.subtitle}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white max-w-4xl leading-tight">
            {content.title}
          </h1>
        </div>
      </section>

      {/* Content Area */}
      <div className="bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 max-w-7xl mx-auto">
            
            {/* Sidebar - Table of Contents */}
            <aside className="lg:col-span-1 order-2 lg:order-1">
              <div className="lg:sticky lg:top-24">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4">
                  In This Article
                </h3>
                <nav className="space-y-1">
                  {content.tableOfContents.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setActiveSection(index);
                        document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className={`block w-full text-left text-sm py-2 px-3 rounded-md transition-colors ${
                        activeSection === index
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </nav>

                {/* Quick CTA in sidebar */}
                <div className="mt-8 bg-slate-900 rounded-xl p-5">
                  <h4 className="text-white font-bold mb-2">Ready to Get Started?</h4>
                  <p className="text-gray-300 text-sm mb-4">Speak with a lending specialist today.</p>
                  <Button
                    data-testid="sidebar-apply-btn"
                    onClick={() => setIsFormOpen(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Apply Now
                  </Button>
                  <a
                    href="tel:903-476-2937"
                    className="flex items-center justify-center gap-2 text-gray-300 hover:text-white text-sm mt-3 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    903-476-2937
                  </a>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-3 order-1 lg:order-2">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {content.introduction}
                </p>
              </div>

              {/* Content Sections */}
              {content.sections.map((section, index) => (
                <div
                  key={index}
                  id={`section-${index}`}
                  className="mb-16 scroll-mt-24"
                  data-testid={`loan-section-${index}`}
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                    {section.heading}
                  </h2>

                  <p className="text-gray-700 leading-relaxed mb-6">
                    {section.content}
                  </p>

                  {/* Image */}
                  {section.image && (
                    <div className="mb-8 rounded-xl overflow-hidden">
                      <img
                        src={section.image}
                        alt={section.imageAlt || section.heading}
                        className="w-full h-auto max-h-[500px] object-contain"
                      />
                    </div>
                  )}

                  {/* Bullet Points */}
                  {section.bulletPoints && (
                    <ul className="space-y-3 mb-6">
                      {section.bulletPoints.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="mt-1.5 w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Numbered List */}
                  {section.numberedList && (
                    <div className="space-y-6 mb-6">
                      {section.numberedList.map((item, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {i + 1}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Key Details Grid */}
                  {section.keyDetails && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
                      {section.keyDetails.map((detail, i) => {
                        const IconComponent = iconMap[detail.label] || FileText;
                        return (
                          <div
                            key={i}
                            className="bg-gray-50 border border-gray-100 rounded-xl p-4 hover:border-blue-200 transition-colors"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <IconComponent className="h-4 w-4 text-blue-600" />
                              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                                {detail.label}
                              </span>
                            </div>
                            <span className="text-lg font-bold text-gray-900">{detail.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}

              {/* Final Thoughts */}
              <div className="mb-16" id={`section-${content.sections.length}`}>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                  Final Thoughts
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {content.finalThoughts}
                </p>
              </div>

              {/* Partner CTA Section */}
              <div
                className="bg-slate-900 rounded-2xl p-8 md:p-12"
                id={`section-${content.sections.length + 1}`}
                data-testid="loan-detail-cta"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Partner with Close Now Lending
                </h2>
                <p className="text-gray-300 leading-relaxed mb-8 max-w-2xl">
                  {content.ctaText}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    data-testid="loan-detail-apply-btn"
                    onClick={() => setIsFormOpen(true)}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                  >
                    <span>Get Started Today</span>
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                  <a href="tel:903-476-2937">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 px-8"
                    >
                      <Phone className="h-5 w-5 mr-2" />
                      Call 903-476-2937
                    </Button>
                  </a>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      <Footer />

      <InquiryForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        loanType={content.subtitle}
      />
    </div>
  );
};

export default LoanDetailPage;
